import React, { useMemo } from "react";
import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card.js";
import BarChart from "components/charts/BarChart";
import { GOLD_TEXT } from "constants/brandColors";

// `rows` is /reports/news/views-by-category's data, already sorted desc by
// totalViews: [{categoryId, slug, name, totalViews, articleCount}]. `name`
// comes back translated in whatever `locale` was requested and can be null
// if that category has no translation row for it — fall back to slug/id.
export default function ViewsByCategoryChart({ rows }) {
  const { t } = useTranslation();

  const { categories, chartData } = useMemo(() => {
    const safeRows = rows || [];
    return {
      categories: safeRows.map((r) => r.name || r.slug || `#${r.categoryId}`),
      chartData: [
        { name: t("Views"), data: safeRows.map((r) => r.totalViews) },
        { name: t("Articles"), data: safeRows.map((r) => r.articleCount) },
      ],
    };
  }, [rows, t]);

  const chartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      colors: ["#C7B45E", "#4318FF"],
      dataLabels: { enabled: false },
      tooltip: { theme: "light" },
      legend: { show: true, position: "top" },
      xaxis: {
        categories,
        labels: { style: { colors: "#A3AED0", fontSize: "12px" }, rotate: -20 },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: "#A3AED0" } } },
      grid: { strokeDashArray: 5 },
      plotOptions: { bar: { borderRadius: 6, columnWidth: "50%" } },
    }),
    [categories]
  );

  return (
    <Card w="100%" p="20px">
      <Text color={GOLD_TEXT} fontSize="xl" fontWeight="700" mb="10px">
        {t("Views by Category")}
      </Text>
      {rows?.length ? (
        <Box h="320px">
          <BarChart chartData={chartData} chartOptions={chartOptions} />
        </Box>
      ) : (
        <Text color="gray.500" textAlign="center" py={10}>{t("No data found")}</Text>
      )}
    </Card>
  );
}
